import { WarningIcon } from '@phosphor-icons/react'
import { useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { match, P } from 'ts-pattern'
import { DefaultButton } from '../../../components/buttons/default-button.tsx'
import { ErrorBoundaryFallback } from '../../../components/error-boundaries/error-boundary-fallback.tsx'
import { ErrorBoundary } from '../../../components/error-boundaries/error-boundary.tsx'
import { PermissionNotice } from '../../../components/permission-notice.tsx'
import { useToastService } from '../../../services/toast-service.tsx'
import {
  useAdminRole,
  useAllIntegrations,
  useGetIntegrationsAllowedMethods,
  useIntegrationCount,
  useOnDemotion,
} from '../../../utils/api-client/api-hooks.ts'
import type { Integration } from '../../../utils/api-client/api-schemas.ts'
import { ErrorDetail } from '../../../utils/error-handling/error-detail.tsx'
import { pluralize } from '../../../utils/text-utils.ts'
import { DocumentationDrawer } from './documentation-drawer.tsx'
import { InstanceCard, ReadOnlyInstanceCard } from './instance-card.tsx'
import type { CatalogEntry } from './integration-instance-utils.ts'
import {
  getAllInstances,
  getIsNeedsAttention,
  SCM_INTEGRATION_CATALOG,
  TOOL_INTEGRATION_CATALOG,
} from './integration-instance-utils.ts'
import * as styles from './integrations-tab.css.ts'
import { ManageDrawer } from './manage-drawer.tsx'
import { SetupDrawer } from './setup-drawer.tsx'
import { ToolCatalogCard } from './tool-catalog-card.tsx'

/**
 * How many integrations the tab badge reports.
 *
 * Every instance the tab lists, managed and system alike. A system instance is configured in the
 * Pixee Enterprise Server admin dashboard rather than here, but it is still an integration this
 * installation has, and the badge has to agree with the number of cards below it. The count reads
 * the `total` of a one-item page, so it costs one small request rather than every page of the
 * collection.
 *
 * Non-suspense so the badge renders while the request is in flight rather than suspending the page;
 * an unsettled count reads as 0, which is what the badge showed before it loaded anyway. A failed
 * count with nothing cached is `undefined` rather than 0, so the badge does not claim the org has
 * no integrations above the tab's load-error card.
 *
 * This can still exceed the number of cards `IntegrationsTabContent` draws below. The badge is the
 * platform's `total`, while the cards come from `_embedded.items` after
 * `IntegrationPaginatedResponseSchema` has dropped every item whose `type` this build does not
 * model — so an org running a tool the platform gained ahead of the front end sees a badge of 5 over
 * three cards. `total` is kept because it is the honest count of what the org has configured, and
 * because the badge renders on every organization page: making it agree with the cards would mean
 * paging the whole collection to draw one number.
 */
export const useConfiguredIntegrationCount = (): number | undefined => {
  const { count, isError } = useIntegrationCount()
  if (count === undefined && isError) {
    return undefined
  }
  return count ?? 0
}

/**
 * Whether this installation opted into in-product integration management.
 *
 * The collection answers an OPTIONS probe with the methods it supports; an installation serving
 * integrations read-only omits POST and answers every write with 405. Composing that into a single
 * boolean belongs here rather than in `api-hooks`, which stays a thin wrapper over the endpoint.
 * A probe that errors is read as enabled: a backend that does not answer OPTIONS at all should not
 * cost the whole tab its affordances, and a write that turns out to be refused surfaces its own 405.
 */
const useIsManagementEnabled = (): { isEnabled: boolean; isPending: boolean } => {
  const { data: allowedMethods, isPending, isError } = useGetIntegrationsAllowedMethods()
  return { isEnabled: isError || (allowedMethods?.includes('POST') ?? false), isPending }
}

// Source providers first: a repository reaches Pixee through one, and a scanner has nothing to
// report on until it has. They were absent from this catalog while a deployment could have only one
// of each, connected at install time through the admin console.
const CATALOG_GROUPS = [
  { heading: 'Source Providers', entries: SCM_INTEGRATION_CATALOG },
  { heading: 'Scanner Integrations', entries: TOOL_INTEGRATION_CATALOG },
] as const

type ActiveDrawer =
  | { kind: 'setup'; entry: CatalogEntry; opener?: HTMLElement }
  | { kind: 'docs'; entry: CatalogEntry; opener?: HTMLElement }
  | { kind: 'manage'; integration: Integration; opener: HTMLElement }
  | undefined

export const IntegrationsTab: React.FC = () => {
  return (
    <ErrorBoundary fallbackTitle="Unable to load integrations">
      <IntegrationsTabContent />
    </ErrorBoundary>
  )
}

const IntegrationsTabContent: React.FC = () => {
  const [activeDrawer, setActiveDrawer] = useState<ActiveDrawer>(undefined)

  const {
    integrations,
    isLoading: isLoadingIntegrations,
    firstPageError,
    hasFailedPage,
    refetch: refetchIntegrations,
  } = useAllIntegrations()

  // In-product management is opt-in per install. Where it is off the backend rejects writes, so the
  // tab collapses to a read-only list — no catalog, no create/edit/delete affordances. Wait for the
  // probe before deciding so the tab never flashes the read-only layout while it is still in flight.
  const { isEnabled: isManagementEnabled, isPending: isManagementProbePending } = useIsManagementEnabled()

  // Two independent gates. `Allow` says whether this deployment supports writes and is the same for
  // every caller; the role says whether this caller may make them. Only where both hold is the tab
  // manageable. The role is waited on only where it can change the answer, so an admin never sees
  // the read-only layout flash before the managed one.
  const { isAdmin, isRoleUnavailable, isRolePending, hasWriteAccess } = useAdminRole()
  const isManageable = isManagementEnabled && hasWriteAccess

  const { addToast } = useToastService()

  // Not memoised: `useAllIntegrations` rebuilds its array on every render, so any dependency on it
  // changes identity every time and a `useMemo` here would recompute anyway while reading as though
  // it did not. The sort is cheap against a list this page already renders in full.
  const instances = getAllInstances(integrations)

  const closeDrawer = () => setActiveDrawer(undefined)

  // Retry unmounts its own button while the list reloads, which drops keyboard focus to <body>. Hand
  // it to the region that replaced the button, unless the user has already moved it somewhere else.
  const listRegionRef = useRef<HTMLDivElement>(null)
  const handleRetry = async () => {
    await refetchIntegrations()
    if (document.activeElement === null || document.activeElement === document.body) {
      listRegionRef.current?.focus()
    }
  }

  // Say why, since a drawer vanishing unannounced reads as a bug. A refused save lands here after its
  // own sticky 403 toast, so this one is the follow-on that explains the closed drawer.
  useOnDemotion({
    isWriteFormOpen: activeDrawer?.kind === 'setup' || activeDrawer?.kind === 'manage',
    onDemote: () => {
      addToast({
        message: <>Your permissions changed. Only administrators can change integrations.</>,
        variant: 'error',
      })
      closeDrawer()
    },
  })

  // The drawers are siblings of the body rather than children of it, so nothing the body switches
  // on can unmount them. `isLoadingIntegrations` is not first-load-only: the page walk inside
  // `useAllIntegrations` derives its page keys from the size each response reports, so a background
  // refetch — every return to this browser tab, under the global `staleTime: 0` — can raise it again
  // with the list already on screen. While a drawer rendered inside the body, that flash threw away
  // half-entered credentials and replayed the drawer's open animation, because `activeDrawer` lives
  // out here and brought it straight back empty.
  return (
    <>
      <div ref={listRegionRef} tabIndex={-1} role="region" aria-label="Integrations">
        {match({
          isPending: isManagementProbePending || isLoadingIntegrations || (isManagementEnabled && isRolePending),
          firstPageError,
          isManageable,
        })
          .with({ isPending: true }, () => <div className={styles.loading}>Loading…</div>)
          .with({ firstPageError: P.nonNullable }, ({ firstPageError }) => (
            <ErrorBoundaryFallback
              title="Unable to load integrations"
              detail={<ErrorDetail error={firstPageError} />}
              onRetry={handleRetry}
            />
          ))
          .with({ isManageable: false }, () => (
            <ReadOnlyIntegrationsList
              integrations={instances}
              isIncomplete={hasFailedPage}
              notice={
                isManagementEnabled ? (
                  <PermissionNotice isAdmin={isAdmin} isRoleUnavailable={isRoleUnavailable}>
                    Only administrators can add, edit, or remove integrations.
                  </PermissionNotice>
                ) : undefined
              }
            />
          ))
          .otherwise(() => (
            <ManagedIntegrationsList
              instances={instances}
              isIncomplete={hasFailedPage}
              onOpenDrawer={setActiveDrawer}
              managedIntegrationId={activeDrawer?.kind === 'manage' ? activeDrawer.integration.id : undefined}
            />
          ))}
      </div>

      {activeDrawer?.kind === 'setup' && (
        <SetupDrawer entry={activeDrawer.entry} opener={activeDrawer.opener} onClose={closeDrawer} />
      )}
      {activeDrawer?.kind === 'docs' && (
        <DocumentationDrawer
          entry={activeDrawer.entry}
          opener={activeDrawer.opener}
          onClose={closeDrawer}
          // The docs stay readable after a demotion, but the jump to Setup goes with the role. The
          // Setup drawer receives the docs opener, so closing it returns focus to the
          // "View {tool} documentation" button the user started the flow from.
          onAddIntegration={
            isManageable
              ? () => setActiveDrawer({ kind: 'setup', entry: activeDrawer.entry, opener: activeDrawer.opener })
              : undefined
          }
        />
      )}
      {activeDrawer?.kind === 'manage' && (
        <ManageDrawer integration={activeDrawer.integration} opener={activeDrawer.opener} onClose={closeDrawer} />
      )}
    </>
  )
}

type ManagedIntegrationsListProps = {
  instances: Integration[]
  isIncomplete: boolean
  onOpenDrawer: (drawer: ActiveDrawer) => unknown
  /** The instance whose Manage drawer is open, if any. */
  managedIntegrationId?: string
}

// What the tab shows where in-product management is on: the configured instances, then the catalog
// of what else can be connected.
const ManagedIntegrationsList: React.FC<ManagedIntegrationsListProps> = ({
  instances,
  isIncomplete,
  onOpenDrawer,
  managedIntegrationId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const isAvailableHidden = searchParams.get('available') === 'hidden'

  const needsAttentionCount = instances.filter(getIsNeedsAttention).length

  const toggleAvailable = () =>
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous)
        if (isAvailableHidden) {
          next.delete('available')
        } else {
          next.set('available', 'hidden')
        }
        return next
      },
      { replace: true }
    )

  // Until the tab has something to list the catalog is the whole point of it, so it renders on its
  // own. Once anything is connected it moves below an "Available" division that can be hidden. A
  // system instance counts here even though the tab badge does not count it: the deployment plainly
  // is connected to something, so "No integrations" would be untrue.
  const isEmpty = instances.length === 0

  return (
    <div className={styles.container}>
      {isIncomplete && <IncompleteListBanner />}
      {needsAttentionCount > 0 && (
        <div className={styles.attentionBanner} role="status">
          <WarningIcon size={20} weight="light" className={styles.attentionBannerIcon} />
          <p className={styles.attentionBannerText}>
            <span className={styles.attentionBannerCount}>{needsAttentionCount}</span>{' '}
            {pluralize(needsAttentionCount, 'integration needs', 'integrations need')} your attention
          </p>
        </div>
      )}

      {isEmpty ? (
        <>
          <div className={styles.emptyStateCard} data-testid="integrations-empty-state">
            <p className={styles.emptyStateTitle}>No integrations</p>
            <p className={styles.emptyStateSubtitle}>Connect a tool to get started</p>
          </div>
          <CatalogGrid entries={CATALOG_GROUPS.flatMap(group => group.entries)} onOpenDrawer={onOpenDrawer} />
        </>
      ) : (
        <>
          <div className={styles.cardGrid}>
            {instances.map(integration => (
              <InstanceCard
                key={integration.id}
                integration={integration}
                onManage={opener => onOpenDrawer({ kind: 'manage', integration, opener })}
                isSelected={integration.id === managedIntegrationId}
              />
            ))}
          </div>

          {/* The toggle breaks the divider, so it reads as the hinge between the two sections. */}
          <div className={styles.availableToggleRow}>
            <DefaultButton
              buttonType={isAvailableHidden ? 'primary' : 'secondary'}
              size="small"
              onClick={toggleAvailable}
            >
              {isAvailableHidden ? 'Show available' : 'Hide available'}
            </DefaultButton>
          </div>

          {!isAvailableHidden && (
            <>
              <h2 className={styles.availableHeading}>Available</h2>
              <div className={styles.catalogGroups}>
                {CATALOG_GROUPS.map(group => (
                  <section key={group.heading}>
                    <h3 className={styles.catalogGroupHeading}>{group.heading}</h3>
                    <CatalogGrid entries={group.entries} onOpenDrawer={onOpenDrawer} />
                  </section>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

type CatalogGridProps = {
  entries: CatalogEntry[]
  onOpenDrawer: (drawer: ActiveDrawer) => unknown
}

const CatalogGrid: React.FC<CatalogGridProps> = ({ entries, onOpenDrawer }) => (
  <div className={styles.catalogGrid}>
    {entries.map(entry => (
      <ToolCatalogCard
        key={entry.id}
        entry={entry}
        onAdd={opener => onOpenDrawer({ kind: 'setup', entry, opener })}
        onViewDocs={opener => onOpenDrawer({ kind: 'docs', entry, opener })}
      />
    ))}
  </div>
)

// What the tab shows where in-product integration management is off, or the caller may not use
// it: name and type only, nothing to act on. Lists the same instances the managed grid does, so the
// two views cannot disagree about what the deployment is connected to. On an installation that
// never opted in, that list is exactly the admin-console connections, which is what this view
// exists to show.
type ReadOnlyIntegrationsListProps = {
  integrations: Integration[]
  isIncomplete: boolean
  /** Why the list is read-only, where the caller's role is the reason rather than the deployment. */
  notice: React.ReactNode
}

const ReadOnlyIntegrationsList: React.FC<ReadOnlyIntegrationsListProps> = ({ integrations, isIncomplete, notice }) => (
  <div className={styles.container}>
    {notice}
    {isIncomplete && <IncompleteListBanner />}
    {integrations.length === 0 ? (
      <div className={styles.emptyStateCard} data-testid="integrations-empty-state">
        <p className={styles.emptyStateTitle}>No integrations</p>
      </div>
    ) : (
      <div className={styles.cardGrid}>
        {integrations.map(integration => (
          <ReadOnlyInstanceCard key={integration.id} integration={integration} />
        ))}
      </div>
    )}
  </div>
)

const IncompleteListBanner: React.FC = () => (
  <div className={styles.attentionBanner} role="status">
    <WarningIcon size={20} weight="light" className={styles.attentionBannerIcon} />
    <p className={styles.attentionBannerText}>Some integrations could not be loaded. This list may be incomplete.</p>
  </div>
)
